import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout/Layout';
import { documentConfigAPI } from '../services/documentConfigAPI';
import { toast } from 'react-hot-toast';

// Componentes modulares
import FileUploadArea from '../components/DataExtractor/FileUploadArea';
import FileQueue from '../components/DataExtractor/FileQueue';
import DocumentTypeSelector from '../components/DataExtractor/DocumentTypeSelector';
import DocumentFields from '../components/DataExtractor/DocumentFields';
import PageSelector from '../components/DataExtractor/PageSelector';
import ExtractionButton from '../components/DataExtractor/ExtractionButton';
import ResultsTable from '../components/DataExtractor/ResultsTable';
import ExtractionHistory from '../components/DataExtractor/ExtractionHistory';
import UserQuotaDisplay from '../components/DataExtractor/UserQuotaDisplay';
import ExtractionMethodSelector from '../components/DataExtractor/ExtractionMethodSelector';

// Hooks personalizados
import { useFileUpload, useExtraction } from '../hooks/useExtraction';
import useAppConfig from '../hooks/useAppConfig';

// Utilidades
import { exportData } from '../utils/exportUtils';

// Estilos
import '../styles/DataExtractor.css';

const ExtractorDatos = () => {
  const { user } = useAuth();
  
  // Estados principales
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [selectedDocumentFields, setSelectedDocumentFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxPagesPerPdf, setMaxPagesPerPdf] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage] = useState(10);
  const [quotaRefreshTrigger, setQuotaRefreshTrigger] = useState(0);

  // Estado para el método de extracción
  const [extractionMethod, setExtractionMethod] = useState('ocr_text');

  // Configuración dinámica de la aplicación
  const { config: appConfig, loading: configLoading } = useAppConfig();

  // Hooks personalizados
  const {
    uploadedFiles,
    dragOver,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    removeFile,
    clearAllFiles,
    resetUploadState,
    // Exponer setUploadedFiles para la subida individual
    setUploadedFiles
  } = useFileUpload();

  // Callbacks estables para los hooks
  const onExtractionComplete = useCallback(() => {
    clearAllFiles();
    // Usar reset adicional para asegurar estado limpio
    if (resetUploadState) {
      resetUploadState();
    }
  }, [clearAllFiles, resetUploadState]);

  const onQuotaRefresh = useCallback(() => {
    setQuotaRefreshTrigger(prev => prev + 1);
  }, []);

  const {
    extractionResults,
    extractionHistory,
    uploading,
    progress,
    clearResults,
    clearHistory,
    uploadFilesIndividually
  } = useExtraction(documentTypes, selectedDocumentFields, maxPagesPerPdf, onExtractionComplete, onQuotaRefresh);

  // Cargar tipos de documentos según el rol del usuario
  useEffect(() => {
    const loadDocumentTypes = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        let types = [];
        
        if (user.role === 'admin') {
          // Para administradores: obtener plantillas públicas que han creado
          try {
            const response = await documentConfigAPI.getPublicTemplates();
            // Verificar si la respuesta tiene la estructura esperada
            if (response && response.items) {
              types = Array.isArray(response.items) ? response.items : [];
            } else if (Array.isArray(response)) {
              types = response;
            } else {
              types = [];
            }
          } catch (adminError) {
            types = [];
          }
        } else {
          // Para usuarios regulares: obtener solo sus documentos privados
          try {
            const myTypes = await documentConfigAPI.getMyDocumentTypes().catch(err => {
              return [];
            });
            // Procesar myTypes
            if (myTypes && myTypes.items) {
              types = Array.isArray(myTypes.items) ? myTypes.items : [];
            } else if (Array.isArray(myTypes)) {
              types = myTypes;
            } else {
              types = [];
            }
          } catch (userError) {
            types = [];
          }
        }
        
        // Asegurar que types sea siempre un array
        const safeTypes = Array.isArray(types) ? types : [];
        
        // Actualizar ambos estados juntos usando un efecto secundario
        setDocumentTypes(safeTypes);
        // Solo actualizar el tipo seleccionado si no hay uno ya seleccionado
        if (safeTypes.length > 0 && !selectedDocumentType) {
          setTimeout(() => {
            setSelectedDocumentType(safeTypes[0].id.toString());
          }, 0);
        }
        
      } catch (error) {
        toast.error('Error al cargar tipos de documentos');
        setDocumentTypes([]);
      } finally {
        setLoading(false);
      }
    };

    loadDocumentTypes();
  }, [user, selectedDocumentType]);

  // Cargar campos del documento seleccionado
  useEffect(() => {
    const loadDocumentFields = async () => {
      if (!selectedDocumentType || !user) {
        setSelectedDocumentFields([]);
        return;
      }

      try {
        const fields = await documentConfigAPI.getFieldsByRole(selectedDocumentType, user.role);
        if (Array.isArray(fields)) {
          setSelectedDocumentFields(fields);
        } else {
          setSelectedDocumentFields([]);
        }
      } catch (error) {
        setSelectedDocumentFields([]);
      }
    };

    loadDocumentFields();
  }, [selectedDocumentType, user]);

  // Lógica de paginación
  const totalResults = extractionResults.length;
  const totalPages = Math.ceil(totalResults / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentResults = extractionResults.slice(startIndex, endIndex);

  // Funciones de paginación
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () => setCurrentPage(Math.max(1, currentPage - 1));
  const goToNextPage = () => setCurrentPage(Math.min(totalPages, currentPage + 1));

  // Manejar extracción con subida individual por archivo
  const onStartExtraction = () => {
    // Filtrar solo archivos en estado 'Pendiente'
    const pendingFiles = uploadedFiles.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast.error('No hay archivos pendientes para procesar');
      return;
    }
    uploadFilesIndividually(selectedDocumentType, pendingFiles, setUploadedFiles, maxPagesPerPdf, extractionMethod);
  };

  // Exportar resultados
  const onExportResults = async (format) => {
    if (extractionResults.length === 0) {
      toast.error('No hay resultados para exportar');
      return;
    }

    try {
      const fileName = `extraccion_${selectedDocumentType}_${new Date().toISOString().split('T')[0]}`;
      const result = await exportData(extractionResults, fileName, format, selectedDocumentFields);
      if (result.success) {
        toast.success(`Archivo ${result.fileName} descargado`);
      } else {
        toast.error(result.error || 'Error al exportar');
      }
    } catch (error) {
      toast.error('Error inesperado al exportar');
    }
  };

  // Descargar desde historial
  const onDownloadHistory = async (historyEntry, format) => {
    try {

      // Transformar los datos del historial al formato correcto para exportación
      const transformedResults = historyEntry.results.map(result => ({
        fileName: result.fileName,
        success: result.success,
        error: result.error,
        extractedData: result.extractedData || {}
      }));
      
      console.log('Datos transformados para exportación:', transformedResults);

      // Obtener los campos del tipo de documento específico del historial
      let fieldsForExport = [];
      try {
        // Buscar el tipo de documento por ID (preferido) o por nombre (fallback)
        let documentType = null;
        
        if (historyEntry.document_type_id) {
          // Buscar por ID (más confiable)
          documentType = documentTypes.find(dt => dt.id === historyEntry.document_type_id);
        }
        
        if (!documentType) {
          // Fallback: buscar por nombre
          documentType = documentTypes.find(dt => 
            dt.nombre === historyEntry.documentType || 
            dt.nombre === historyEntry.document_type ||
            dt.id.toString() === historyEntry.documentType ||
            dt.id.toString() === historyEntry.document_type
          );
        }
        
        if (documentType && user) {
          const { documentConfigAPI } = await import('../services/documentConfigAPI');
          const fields = await documentConfigAPI.getFieldsByRole(documentType.id, user.role);
          if (Array.isArray(fields) && fields.length > 0) {
            fieldsForExport = fields;
          }
        }
      } catch (error) {
        // Error al obtener campos del tipo de documento
      }
      
      // Si no se pudieron obtener los campos del tipo de documento, extraer de los datos
      if (fieldsForExport.length === 0) {
        const fieldsFromHistory = new Set();
        transformedResults.forEach(result => {
          if (result.extractedData && typeof result.extractedData === 'object') {
            Object.keys(result.extractedData).forEach(field => {
              if (field && field !== 'null' && field !== 'undefined') {
                fieldsFromHistory.add(field);
              }
            });
          }
        });
        
        fieldsForExport = Array.from(fieldsFromHistory).map(fieldName => ({
          nombre: fieldName,
          tipo: 'text' // tipo por defecto
        }));
      }
      
      // Si aún no hay campos, usar campos por defecto
      if (fieldsForExport.length === 0) {
        fieldsForExport = [
          { nombre: 'sin_datos', tipo: 'text' }
        ];
      }
      
      const fileName = `extraccion_historial_${historyEntry.id}_${new Date().toISOString().split('T')[0]}`;
      const result = await exportData(transformedResults, fileName, format, fieldsForExport);
      if (result.success) {
        toast.success(`Archivo ${result.fileName} descargado`);
      } else {
        toast.error(result.error || 'Error al exportar');
      }
    } catch (error) {
      toast.error('Error inesperado al exportar');
    }
  };

  // Limpiar resultados con reset de página
  const onClearResults = () => {
    clearResults();
    setCurrentPage(1);
  };

  return (
    <Layout>
      {/* Header de bienvenida */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h2 mb-2 fw-bold text-dark">
                ¡Bienvenido, {user?.full_name}!
              </h1>
              <p className="text-muted fs-5">Sube tus archivos PDF para extraer datos automáticamente</p>
            </div>
            <div className="text-muted d-flex align-items-center">
              <i className="bi bi-calendar3 me-2"></i>
              <span className="fw-medium">
                {new Date().toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    
      {/* Área principal de extracción */}
      <div className="row g-4 mb-5">
        {/* Zona de subida de archivos */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="card-title mb-0 fw-semibold">
                <i className="bi bi-cloud-upload me-2 text-primary"></i>
                Subir Documentos PDF
              </h5>
            </div>
            <div className="card-body">
              <FileUploadArea
                dragOver={dragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onFileSelect={handleFileSelect}
              />
              {/* Barra de progreso eliminada por solicitud */}
              <div className="mt-4">
                <FileQueue
                  uploadedFiles={uploadedFiles}
                  onRemoveFile={removeFile}
                  onClearAll={clearAllFiles}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Panel lateral - Opciones de extracción */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-bottom">
              <h5 className="card-title mb-0 fw-semibold">
                <i className="bi bi-gear me-2 text-success"></i>
                Opciones de Extracción
              </h5>
            </div>
            <div className="card-body">
              <DocumentTypeSelector
                documentTypes={documentTypes}
                selectedDocumentType={selectedDocumentType}
                loading={loading}
                user={user}
                onChange={setSelectedDocumentType}
              />

              <DocumentFields selectedDocumentFields={selectedDocumentFields} />


              <PageSelector
                maxPagesPerPdf={maxPagesPerPdf}
                onChange={setMaxPagesPerPdf}
              />

              {/* Selector de método de extracción */}
              <ExtractionMethodSelector
                extractionMethod={extractionMethod}
                setExtractionMethod={setExtractionMethod}
              />

              <ExtractionButton
                selectedDocumentType={selectedDocumentType}
                documentTypes={documentTypes}
                uploadedFiles={uploadedFiles}
                uploading={uploading}
                progress={progress}
                onClick={onStartExtraction}
              />

              {/* Información de cuota del usuario */}
              <div className="mt-2">
                <UserQuotaDisplay refreshTrigger={quotaRefreshTrigger} />
              </div>

              {/* Consejos desplegables para ahorrar espacio */}
              <div className="mt-2 pt-2 border-top">
                <div className="accordion accordion-flush" id="tipsAccordion">
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="tipsHeading">
                      <button 
                        className="accordion-button collapsed py-2 px-0" 
                        type="button" 
                        data-bs-toggle="collapse" 
                        data-bs-target="#tipsCollapse" 
                        aria-expanded="false" 
                        aria-controls="tipsCollapse"
                      >
                        <i className="bi bi-lightbulb text-warning me-2"></i>
                        <span className="fw-semibold">Consejos para Mejores Resultados</span>
                      </button>
                    </h2>
                    <div 
                      id="tipsCollapse" 
                      className="accordion-collapse collapse" 
                      aria-labelledby="tipsHeading" 
                      data-bs-parent="#tipsAccordion"
                    >
                      <div className="accordion-body px-0 py-2">
                        <div className="list-group list-group-flush">
                          <div className="list-group-item border-0 px-0 py-2">
                            <div className="d-flex align-items-start">
                              <i className="bi bi-file-earmark-pdf text-success me-3 mt-1"></i>
                              <small className="text-muted">Use archivos PDF de buena calidad y resolución</small>
                            </div>
                          </div>
                          <div className="list-group-item border-0 px-0 py-2">
                            <div className="d-flex align-items-start">
                              <i className="bi bi-grid-3x2 text-info me-3 mt-1"></i>
                              <small className="text-muted">Los documentos estructurados funcionan mejor</small>
                            </div>
                          </div>
                          <div className="list-group-item border-0 px-0 py-2">
                            <div className="d-flex align-items-start">
                              <i className="bi bi-layers text-warning me-3 mt-1"></i>
                              <small className="text-muted">
                                PDFs multipágina: solo se procesan las primeras {maxPagesPerPdf} página{maxPagesPerPdf > 1 ? 's' : ''}
                              </small>
                            </div>
                          </div>
                          <div className="list-group-item border-0 px-0 py-2">
                            <div className="d-flex align-items-start">
                              <i className="bi bi-speedometer2 text-danger me-3 mt-1"></i>
                              <small className="text-muted">Cada archivo procesado exitosamente consume 1 documento de tu cuota</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resultados de extracción */}
      <div className="row mb-5">
        <div className="col-12">
          <ResultsTable
            extractionResults={extractionResults}
            selectedDocumentFields={selectedDocumentFields}
            currentResults={currentResults}
            startIndex={startIndex}
            endIndex={endIndex}
            totalResults={totalResults}
            currentPage={currentPage}
            totalPages={totalPages}
            onExportResults={onExportResults}
            onClearResults={onClearResults}
            onGoToPage={goToPage}
            onGoToFirstPage={goToFirstPage}
            onGoToLastPage={goToLastPage}
            onGoToPreviousPage={goToPreviousPage}
            onGoToNextPage={goToNextPage}
          />
        </div>
      </div>

      {/* Historial de extracciones */}
      {extractionHistory.length > 0 && (
        <div className="row">
          <div className="col-12">
            <ExtractionHistory
              extractionHistory={extractionHistory}
              onDownload={onDownloadHistory}
              onClearHistory={clearHistory}
            />
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ExtractorDatos;
